import { BarcodeType, DbRef, SupplyItemStatus } from '@caresend/types';
import { errorSuffix, hasStatus } from '@caresend/utils';
import 'firebase/auth';
import '../../database/firebaseConfig/cloudFunctions.js';
import '../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../components/ui/Icon.vue.js';
import '../configuration/icons.js';
import '../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../tracking/index.js';
import '../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { getSupplyItemsFromBatchItem, supplyItemAlreadyInProcedureBagAtThisWaypoint } from './get.js';
import { hasBarcodeType, handleScanningError, handleScanningWarning } from './scanning.js';

const alreadyActivatedSupplyMessage = 'You already activated this supply';
const supplyExpiredMessage = 'This supply expired. Please use another supply.';
const getError = (message) => ({ message, type: 'error' });
const getWarning = (message) => ({ message, type: 'warning' });
const handleScanningFailure = async (failure, barcode) => {
    if (failure.type === 'error') {
        await handleScanningError(failure.message, barcode);
    }
    else {
        await handleScanningWarning(failure.message, barcode);
    }
};
/**
 * If the user is on a single-scan component, and is trying to scan something
 * when something was already scanned, rejects the scan and instructs them as
 * appropriate.
 */
const getSingleScanFailure = (scannedItem, singleScanValidation) => {
    const failedSingleScan = !!singleScanValidation && singleScanValidation.alreadyScannedBarcodeID;
    if (!failedSingleScan)
        return null;
    if (scannedItem.barcode.id === (singleScanValidation === null || singleScanValidation === void 0 ? void 0 : singleScanValidation.alreadyScannedBarcodeID)) {
        return getWarning('You have already scanned the item.');
    }
    const scanDifferentSuffix = singleScanValidation.allowScanDifferentItem
        ? ' If you wish to scan this item instead, click “Remove and scan '
            + 'a different item”.'
        : '';
    return getWarning(`You have already scanned a different item.${scanDifferentSuffix}`);
};
/**
 * If expected barcode type is present, reject scanning if the scanned item
 * is of the wrong barcode type.
 */
const getBarcodeTypeFailure = (scannedItem, expectedBarcodeType) => {
    const failedExpectedBarcodeType = !!expectedBarcodeType && expectedBarcodeType !== scannedItem.barcode.type;
    if (failedExpectedBarcodeType)
        return getWarning('Wrong item');
    return null;
};
/**
 * If expected barcode ID is present, reject scanning if the scanned item
 * has the wrong barcode ID.
 *
 * Suitable for generic (non-supply) barcode validation.
 */
const getBarcodeIDFailure = (scannedItem, expectedBarcodeID) => {
    const failedExpectedBarcodeID = !!expectedBarcodeID && expectedBarcodeID !== scannedItem.barcode.id;
    if (failedExpectedBarcodeID)
        return getWarning('Wrong barcode');
    return null;
};
/**
 * Validation checks available for all scanned items regardless of the type of
 * barcode.
 */
const getGenericScanningFailure = async ({ scannedItem, expectedBarcodeID, expectedBarcodeType, singleScanValidation, }) => {
    const singleScanFailure = getSingleScanFailure(scannedItem, singleScanValidation);
    if (singleScanFailure)
        return singleScanFailure;
    const barcodeTypeFailure = getBarcodeTypeFailure(scannedItem, expectedBarcodeType);
    if (barcodeTypeFailure)
        return barcodeTypeFailure;
    const barcodeIDFailure = getBarcodeIDFailure(scannedItem, expectedBarcodeID);
    if (barcodeIDFailure)
        return barcodeIDFailure;
    return null;
};
/**
 * If criteria are not met for expected supply ID or supply item IDs, reject
 * the scan with `Wrong supply`.
 */
const getExpectedSupplyItemFailure = (supplyItem, supplyID, expectedSupplyID, expectedSupplyItem, expectedSupplyItemIDs) => {
    const failedExpectedSupplyID = !!expectedSupplyID && !expectedSupplyID.includes(supplyID !== null && supplyID !== void 0 ? supplyID : 'Missing');
    const failedExpectedSupplyItemIDs = !!expectedSupplyItemIDs && !expectedSupplyItemIDs.includes(supplyItem.id);
    const failedExpectedSupplyItem = !!expectedSupplyItem && expectedSupplyItem.id !== supplyItem.id;
    if (failedExpectedSupplyID
        || failedExpectedSupplyItemIDs
        || failedExpectedSupplyItem) {
        return getWarning('Wrong supply');
    }
    return null;
};
/**
 * If an expected status was specified and the status is incorrect, reject
 * the scan with an appropriate message.
 */
const getSupplyItemStatusFailure = (supplyItem, expectedSupplyItemStatus) => {
    const failedSupplyItemStatusMatch = (!!expectedSupplyItemStatus
        && !hasStatus(supplyItem, expectedSupplyItemStatus));
    if (!failedSupplyItemStatusMatch)
        return null;
    if (hasStatus(supplyItem, SupplyItemStatus.USED)
        && (hasStatus(expectedSupplyItemStatus, [
            SupplyItemStatus.ACTIVATED,
            SupplyItemStatus.CREATED,
        ]))) {
        return getWarning('This supply is already used');
    }
    if (hasStatus(supplyItem, SupplyItemStatus.CREATED)
        && hasStatus(expectedSupplyItemStatus, SupplyItemStatus.ACTIVATED)) {
        return getWarning('You need to activate this supply in your supplies section first');
    }
    if (hasStatus(supplyItem, SupplyItemStatus.ACTIVATED)
        && hasStatus(expectedSupplyItemStatus, SupplyItemStatus.CREATED)) {
        return getWarning(alreadyActivatedSupplyMessage);
    }
    return getWarning(`The scanned supply has the status “${supplyItem === null || supplyItem === void 0 ? void 0 : supplyItem.status}”, but the `
        + `expected status was “${expectedSupplyItemStatus}”.`);
};
/**
 * If `waypointIdForProcedureBagCheck` was specified reject the scan if the
 * supply item is already in a procedure bag at this waypoint.
 */
const getSupplyItemInProcedureBagFailure = (supplyItem, waypointIdForProcedureBagCheck) => {
    if (!waypointIdForProcedureBagCheck)
        return null;
    const alreadyInBag = supplyItemAlreadyInProcedureBagAtThisWaypoint(waypointIdForProcedureBagCheck, supplyItem.id);
    if (alreadyInBag) {
        return getWarning('The scanned supply has already been added to a procedure bag.'
            + ' Please scan another supply or contact support.');
    }
    return null;
};
/**
 * If a SKU is found matching the scanned item, reject the scan if the SKU is
 * set to inactive.
 *
 * TODO: Consider checking the actual expiry date. If implementing this, we
 * must first fix the setting of expiry date in CSV import scripts, as it is
 * currently inconsistent.
 */
const getSupplyItemExpiryFailure = async (supplyItem) => {
    const scannedItemSku = await getValueOnce(`${DbRef.SKUS}/${supplyItem.skuID}`);
    if (!scannedItemSku) {
        return getError(`Could not find SKU for scanned item. ${errorSuffix}`);
    }
    if (!scannedItemSku.active) {
        return getWarning(supplyExpiredMessage);
    }
    return null;
};
/** Validate a single supply item. */
const getSupplyItemValidationFailure = async ({ supplyID, supplyItemID, expectedSupplyItemStatus, expectedSupplyID, expectedSupplyItem, expectedSupplyItemIDs, waypointIdForProcedureBagCheck, }) => {
    const supplyItem = await getValueOnce(`${DbRef.SUPPLY_ITEMS}/${supplyItemID}`);
    if (!supplyItem) {
        return getError('We could not find that supply item in our database. Please contact support.');
    }
    const expectedSupplyItemFailure = getExpectedSupplyItemFailure(supplyItem, supplyID, expectedSupplyID, expectedSupplyItem, expectedSupplyItemIDs);
    if (expectedSupplyItemFailure)
        return expectedSupplyItemFailure;
    const supplyItemStatusFailure = getSupplyItemStatusFailure(supplyItem, expectedSupplyItemStatus);
    if (supplyItemStatusFailure)
        return supplyItemStatusFailure;
    const supplyItemInProcedureBagFailure = getSupplyItemInProcedureBagFailure(supplyItem, waypointIdForProcedureBagCheck);
    if (supplyItemInProcedureBagFailure)
        return supplyItemInProcedureBagFailure;
    const supplyItemExpiryFailure = await getSupplyItemExpiryFailure(supplyItem);
    if (supplyItemExpiryFailure)
        return supplyItemExpiryFailure;
    return null;
};
/**
 * When scanning an item, we use this method to check if the user is
 * allowed to scan the given item.
 *
 * If the checks fail, this function uses `handleScanningError` or
 * `handleScanningWarning` as appropriate to track the failure, and returns
 * false.
 *
 * If the checks pass, this function returns true.
 */
const validateScannedItem = async (params) => {
    const genericScanningFailure = await getGenericScanningFailure(params);
    if (genericScanningFailure) {
        await handleScanningFailure(genericScanningFailure, params.scannedItem.barcode);
        return false;
    }
    // Batch items
    if (hasBarcodeType(params.scannedItem, BarcodeType.BATCH_ITEM)) {
        const supplyItemIDs = (await getSupplyItemsFromBatchItem(params.scannedItem))
            .map(({ id }) => id);
        const failurePromises = supplyItemIDs.map(async (supplyItemID) => {
            const supplyItemParams = {
                ...params,
                supplyItemID,
                supplyID: params.scannedItem.supplyID,
            };
            const failure = await getSupplyItemValidationFailure(supplyItemParams);
            if (failure) {
                return {
                    ...failure,
                    supplyItemID,
                };
            }
            return null;
        });
        const failures = await Promise.all(failurePromises);
        const firstFailure = failures
            .find((failure) => !!failure);
        if (firstFailure) {
            await handleScanningFailure(firstFailure, { id: firstFailure.supplyItemID, type: BarcodeType.SUPPLY_ITEM });
            return false;
        }
        return true;
    }
    // Supply items
    if (hasBarcodeType(params.scannedItem, BarcodeType.SUPPLY_ITEM)) {
        const supplyItemID = params.scannedItem.barcodeValue;
        const supplyItemParams = {
            ...params,
            supplyID: params.scannedItem.supplyID,
            supplyItemID,
        };
        const failure = await getSupplyItemValidationFailure(supplyItemParams);
        if (failure) {
            await handleScanningFailure(failure, params.scannedItem.barcode);
            return false;
        }
        return true;
    }
    // Shipping labels
    if (hasBarcodeType(params.scannedItem, BarcodeType.SHIPPING_LABEL)
        || hasBarcodeType(params.scannedItem, BarcodeType.GENERIC)) {
        // There are no additional checks beyond the generic checks for shipping
        // labels or generic.
        return true;
    }
    // Unknown barcode type
    await handleScanningError(`There was a problem validating the scanned item. ${errorSuffix}`, params.scannedItem.barcode);
    return false;
};

export { getSupplyItemValidationFailure, validateScannedItem };
